import { useRef, useEffect, useState, useMemo } from 'react'
import { ScrollOverMediaSlices } from '../../../data/prismic-helper'
import styled from '@emotion/styled'
import { InView } from 'react-intersection-observer'
import { PrismicRichText } from '@prismicio/react'
import {
  isInScrollRange,
  useScrollPercentage,
} from '../../../util/scrollPercentage'
import {
  OVERLAY_TEXT_BOX_PADDING,
  OVERLAY_TEXT_BOX_MAX_WIDTH,
} from '../../../theme/theme'
import { BREAKPOINT_MOBILE } from '../../../util/breakpoints'
import { inlineImageSerializer, ViewportHeightSpace } from '../../atoms/Atoms'
import { asText } from '@prismicio/client'

export interface StepTextBoxesProps {
  step: ScrollOverMediaSlices
  stepIndex: number
  setActiveStep: (stepIndex: number) => void
  setStepScrollProgress?: (progress: number) => void
}

export function StepTextBoxes(props: StepTextBoxesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)

  const shouldCalculateScrollPercentage =
    isInView && props.setStepScrollProgress !== undefined
  const scrollPercentage = useScrollPercentage(
    containerRef,
    shouldCalculateScrollPercentage
  )

  useEffect(() => {
    if (shouldCalculateScrollPercentage && isInScrollRange(scrollPercentage)) {
      props.setStepScrollProgress?.(scrollPercentage)
    }
    // 'props' can't be in dependency array since it would trigger endless rendering loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPercentage, isInView, shouldCalculateScrollPercentage])

  const textBoxesMemo = useMemo(() => {
    return props.step.items.map((text, boxIndex) => {
      const placement =
      text.text_placement !== 'left' && text.text_placement !== 'right'
        ? 'center'
        : text.text_placement

      return (
        <div key={boxIndex}>
          <InView
            as="div"
            onChange={(inView) => inView && props.setActiveStep(props.stepIndex)}
          >
            {text.text.length > 0 && (
            // translateZ(0) fixes a strange safari rendering issue: Sometimes the textbox would not be rendered.
              <FloatingTextBox
                placement={placement}
                textboxCss={asText(text.textbox_css)}
                style={{ transform: 'translateZ(0)' }}
              >
                <PrismicRichText field={text.text} components={inlineImageSerializer} />
              </FloatingTextBox>
            )}
          </InView>

          <ViewportHeightSpace />
        </div>
      )
    })
  }, [props])

  return (
    <InView as="div" onChange={(inView) => setIsInView(inView)}>
      <div ref={containerRef}>{textBoxesMemo}</div>
    </InView>
  )
}

interface FloatingTextBoxProps {
  placement: 'left' | 'right' | 'center'
  textboxCss: string
}

const FloatingTextBox = styled.div<FloatingTextBoxProps>`
  position: relative;

  padding: ${OVERLAY_TEXT_BOX_PADDING};
  max-width: min(${OVERLAY_TEXT_BOX_MAX_WIDTH}, 100%);
  overflow-wrap: break-word;
  margin: auto;
  pointer-events: auto;

  ul,
  ol {
    padding: 0 ${OVERLAY_TEXT_BOX_PADDING};
  }

  ${(props) =>
    props.placement === 'left' &&
    `
    margin-left: 4em;
  `}

  ${(props) =>
    props.placement === 'right' &&
    `
    margin-right: 4em;
  `}

  @media ${BREAKPOINT_MOBILE} {
    margin: 0;
    max-width: 100%;
    width: 100%;
  }

  background: black;
  * {
    color: white;
  }
  ${({ theme }) => theme.scrollOverMediaTextboxesCss}
  ${({ textboxCss }) => textboxCss}
`
